import { dispatchAction } from "../tree/tree.js";
import { longPressHandler } from "../handler/longpress.js";
import { getKey } from "./keybinds.js";
//#region src/dom/listener.ts
const DEFAULT_LONG_PRESS_THRESHOLD = 500;
/**
* Registers a keyboard listener for a given target and keybinds.
*
* This handler handles long presses for nodes with {@link longPressHandler}.
*
* @param tree - The navigation tree.
* @param target - The target to register the listener on.
* @param keybinds - The keybinds to use.
*
* @returns A function to unregister the listener.
*
* @see {@link Keybinds}
*/
function registerKeyboardListener(tree, target, keybinds) {
	const dispatchPending = (longpress) => {
		if (pending == null) return;
		const action = keybinds(pending.event, { longpress });
		if (action == null) return;
		dispatchAction(tree, action);
		clearPending();
	};
	let pending = null;
	const clearPending = () => {
		if (pending == null) return;
		window.clearTimeout(pending.timer);
		pending = null;
	};
	const onKeyDown = (e) => {
		if (getKey(e) == null) return;
		const action = keybinds(e);
		if (action == null) return;
		e.preventDefault();
		const longPress = longPressHandler.query(tree, tree.focus);
		if (longPress == null || !shouldEnableLongPress(longPress, action)) {
			dispatchAction(tree, action);
			return;
		}
		if ("repeat" in e && e.repeat === true) return;
		dispatchPending(false);
		pending = {
			timer: window.setTimeout(() => {
				dispatchPending(true);
			}, longPress.threshold ?? DEFAULT_LONG_PRESS_THRESHOLD),
			event: e
		};
	};
	const onKeyUp = (e) => {
		if (pending == null) return;
		const key = getKey(e);
		if (key == null || pending.event.key !== key) return;
		e.preventDefault();
		dispatchPending(false);
	};
	const onCancel = () => {
		clearPending();
	};
	target.addEventListener("keydown", onKeyDown);
	target.addEventListener("keyup", onKeyUp);
	window.addEventListener("blur", onCancel);
	window.addEventListener("visibilitychange", onCancel);
	return () => {
		clearPending();
		target.removeEventListener("keydown", onKeyDown);
		target.removeEventListener("keyup", onKeyUp);
		window.removeEventListener("blur", onCancel);
		window.removeEventListener("visibilitychange", onCancel);
	};
}
function shouldEnableLongPress(longPress, action) {
	if (typeof longPress.enabled === "function") return longPress.enabled(action);
	return longPress.enabled ?? true;
}
//#endregion
export { registerKeyboardListener };

//# sourceMappingURL=listener.js.map